import { FilterIcon } from '@/_assets/icon-components'
import {
  Box,
  Button,
  Center,
  Circle,
  Flex,
  Float,
  Link,
  Text,
} from '@chakra-ui/react'
import { useTranslations } from 'next-intl'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname, useRouter } from '@/i18n/navigation'
import { useSearchParams } from 'next/navigation'
import BottomSheet from '@/_components/Sheet'
import Sorted from './components/Sorted'
import Tags from './components/Tags'

const FilterMobile = () => {
  const router = useRouter()
  const t = useTranslations()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Extract query parameters
  const tagIds = searchParams.get('tag_ids')
  const sortValue = searchParams.get('sort')

  // State management
  const [open, setOpen] = useState(false)
  const [sort, setSort] = useState('')
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Memoize filter options to prevent unnecessary re-renders
  const filterOptions = useMemo(
    () => [
      {
        id: '1',
        label: t('filter_for_title'),
        value: 'title.ru|asc',
      },
      {
        id: '2',
        label: t('filter_for_rate'),
        value: 'rate|asc',
      },
      {
        id: '3',
        label: t('filter_for_out_price_asc'),
        value: 'out_price|asc',
      },
      {
        id: '4',
        label: t('filter_for_out_price_desc'),
        value: 'out_price|desc',
      },
    ],
    [t]
  )

  // Get filter option ID by value
  const getIdByValue = useCallback(
    (value: string) => {
      const option = filterOptions.find((option) => option.value === value)
      return option ? option.id : '0'
    },
    [filterOptions]
  )

  // Handle sorting change
  const handleSortChange = useCallback((newSort: string | null) => {
    setSort(newSort ?? '')
  }, [])

  // Handle tags change
  const handleTagsChange = useCallback((tags: string[]) => {
    setSelectedTags(tags)
  }, [])

  // Clear all filters
  const handleClearFilter = useCallback(() => {
    setSelectedTags([])
    setSort('')
    router.push(pathname, { scroll: false })
    setOpen(false)
  }, [router, pathname])

  // Apply filters
  const handleApplyFilter = useCallback(() => {
    const queryParams = new URLSearchParams()

    if (selectedTags.length > 0) {
      queryParams.append('tag_ids', selectedTags.join(','))
    }

    if (sort) {
      queryParams.append('sort', sort)
    }

    const newPath = queryParams.toString()
      ? `${pathname}?${queryParams.toString()}`
      : pathname

    router.push(newPath, { scroll: false })
    setOpen(false)
  }, [selectedTags, sort, pathname, router])

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () => Boolean(tagIds || sortValue),
    [tagIds, sortValue]
  )

  // Sync state with URL parameters
  useEffect(() => {
    // Update tags from URL
    if (tagIds && tagIds.length > 0) {
      setSelectedTags(tagIds.split(','))
    } else {
      setSelectedTags([])
    }

    // Update sort from URL
    if (sortValue && +getIdByValue(sortValue) > 0) {
      setSort(sortValue)
    } else {
      setSort('')
    }
  }, [tagIds, sortValue, getIdByValue])

  return (
    <>
      <Box position='relative'>
        <Center gap={2} flexDirection='column'>
          <Button
            w='56px'
            height='36px'
            variant='subtle'
            rounded={16}
            px={4}
            bg='#F5F4F3'
            color='#475467'
            onClick={() => setOpen(true)}
            aria-label={t('filters')}
          >
            <FilterIcon color='currentColor' />
          </Button>
        </Center>
        {hasActiveFilters && (
          <Float offsetY={1} offsetX={1}>
            <Circle size='3' bg='primary' color='white' />
          </Float>
        )}
      </Box>

      <BottomSheet isOpen={open} onClose={() => setOpen(false)}>
        <Box px={4} roundedTop='24px' color='text'>
          <Box pt={5}>
            <Flex alignItems='center' justifyContent='space-between' mb={2}>
              <Text fontWeight={600} fontSize='lg' lineHeight='28px'>
                {t('sorting')}
              </Text>
              <Text
                color='primary'
                as={Link}
                fontWeight={400}
                onClick={handleClearFilter}
                cursor='pointer'
              >
                {t('reset')}
              </Text>
            </Flex>

            <Sorted
              value={sort}
              options={filterOptions}
              onValueChange={handleSortChange}
            />

            <Tags values={selectedTags} onChange={handleTagsChange} />
          </Box>

          <Box my={4}>
            <Button
              rounded={16}
              size='xl'
              height='56px'
              bg='primary'
              color='primary.text'
              _hover={{
                bg: 'primary.hover',
              }}
              w='full'
              onClick={handleApplyFilter}
            >
              {t('to_filter')}
            </Button>
          </Box>
        </Box>
      </BottomSheet>
    </>
  )
}

export default React.memo(FilterMobile)
